import {tokens} from '@pleo-io/telescope'
import VisuallyHidden from '@reach/visually-hidden'
import React from 'react'
import styled from 'styled-components'

import Button from '@helios/shared/components/button'
import PrismicLink, {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import {Section, Layout} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import {radii} from '@helios/shared/theme'
import ImageSource from '@helios/shared/types/image'
import mq from '@helios/shared/utils/media-query'

type Logo = {
    logo: ImageSource
    link: PrismicLinkProps
}

type TextLink = {
    text: string
    link: PrismicLinkProps
}

type Props = {
    logo1: Logo
    logo2: Logo
    logo3: Logo
    more: TextLink
    cta: TextLink
    title: string
}

export const Integrations = ({cta, title, ...integrationLogosProps}: Props) => (
    <Section>
        <Layout variant="two-asymmetrical" alignItems="center">
            <Content>
                <Text as="h2" variant="heading-base">
                    {title}
                </Text>
                <Button as={PrismicLink} variant="outline" {...cta.link}>
                    {cta.text}
                </Button>
            </Content>
            <IntegrationLogos {...integrationLogosProps} />
        </Layout>
    </Section>
)

type IntegrationLogosProps = Pick<Props, 'logo1' | 'logo2' | 'logo3' | 'more'>

const IntegrationLogos = ({logo1, logo2, logo3, more}: IntegrationLogosProps) => (
    <IntegrationLogosGrid>
        <IntegrationLogo {...logo1.link} $bgImageUrl={logo1.logo.url}>
            {logo1.logo.alt && <VisuallyHidden>{logo1.logo.alt}</VisuallyHidden>}
        </IntegrationLogo>
        <IntegrationLogo {...logo2.link} $bgImageUrl={logo2.logo.url}>
            {logo2.logo.alt && <VisuallyHidden>{logo2.logo.alt}</VisuallyHidden>}
        </IntegrationLogo>
        <IntegrationLogo {...logo3.link} $bgImageUrl={logo3.logo.url}>
            {logo3.logo.alt && <VisuallyHidden>{logo3.logo.alt}</VisuallyHidden>}
        </IntegrationLogo>
        <MoreIntegrations {...more.link}>
            <Text variant="paragraph-base" center>
                {more.text}
            </Text>
        </MoreIntegrations>
    </IntegrationLogosGrid>
)

const Content = styled.div`
    display: grid;
    row-gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        justify-items: start;
    }
`

const IntegrationLogosGrid = styled.div`
    display: grid;
    padding: 0 ${tokens.spacing24};
    margin: 0 -${tokens.spacing24};
    overflow: auto;
    gap: ${tokens.spacing12};
    grid-template-columns: repeat(4, minmax(136px, 100%));

    ${mq.min('sm')} {
        justify-content: center;
        padding: unset;
        margin: unset;
        overflow: unset;
        grid-template-columns: repeat(2, minmax(0, 180px));
    }

    ${mq.min('lg')} {
        gap: ${tokens.spacing20};
        grid-template-columns: repeat(4, 1fr);
    }
`

interface IntegrationLogoProps {
    $bgImageUrl?: string
}

const IntegrationBox = styled(PrismicLink)`
    aspect-ratio: 1;
    background-color: ${tokens.shade200};
    border: 1px solid transparent;
    border-radius: ${radii.arc8};

    &:hover {
        border-color: ${tokens.shade900};
    }

    &:focus {
        border-color: ${tokens.shade500};
        outline: none;
    }
`
const IntegrationLogoSquare = styled(IntegrationBox)`
    background-repeat: no-repeat;
    background-position: center;
    background-size: 60%;
`

const IntegrationLogo = styled(IntegrationLogoSquare)<IntegrationLogoProps>`
    background-image: url(${(props) => props.$bgImageUrl});
`

const MoreIntegrations = styled(IntegrationBox)`
    display: flex;
    padding: ${tokens.spacing20};
    align-items: center;
    justify-content: center;
`
